/**
 * 从 IndexedDB 提取用户数据
 */

import { defaultDb, type CardRecord, getWeeklyStudyStats, getWeekStart } from '../db/indexedDb';
import type { CardUserData, UserDataPackage, UserSettings, LearningRecord } from './types';
import { getDeviceId, getSyncState, getCloudSyncVersion } from './cloudflareClient';
import { isPublicDeploy } from '../config/features';

/** 从 CardRecord 提取用户数据 */
function extractCardUserData(card: CardRecord): CardUserData {
  // 转换 history 格式，从前一条记录推断 intervalBefore
  const rawHistory = card.history || [];
  const history: LearningRecord[] = rawHistory.map((h, index) => {
    // intervalBefore: 使用前一条记录的 interval，第一条使用默认值 0
    const intervalBefore = index > 0 ? rawHistory[index - 1].interval : 0;
    const easeBefore = index > 0 ? rawHistory[index - 1].ease : card.ease;
    
    return {
      ts: h.ts,
      rating: h.rating,
      mode: h.mode,
      intervalBefore,
      intervalAfter: h.interval,
      easeBefore,
      easeAfter: h.ease,
      answer: h.answerMeta as LearningRecord['answer'],
      notes: h.notes,
    };
  });

  // 提取最佳版本
  const best: CardUserData['best'] = {};
  if (card.speakingMeta?.bestAnswerSummary) {
    best.bestSummary = card.speakingMeta.bestAnswerSummary;
    best.bestTranscript = card.speakingMeta.transcript;
    best.bestRecordingKey = card.speakingMeta.finalRecordingKey;
  }
  if (card.writingMeta?.bestVersion) {
    best.bestVersion = card.writingMeta.bestVersion;
  }

  // 提取草稿
  const drafts: CardUserData['drafts'] = {};
  if (card.speakingMeta?.draftRecordingKey) {
    drafts.draftRecordingKey = card.speakingMeta.draftRecordingKey;
  }

  return {
    cardId: card.id,
    srs: {
      mode: card.mode,
      ease: card.ease,
      interval: card.interval,
      nextReviewDate: card.nextReviewDate,
      consecutiveGood: card.consecutiveGood,
      isRelearn: card.isRelearn,
      relearnBucket: card.relearnBucket,
    },
    history,
    best: Object.keys(best).length > 0 ? best : undefined,
    drafts: Object.keys(drafts).length > 0 ? drafts : undefined,
    createdAt: card.createdAt,
    updatedAt: card.updatedAt,
  };
}

/** 提取完整用户数据包 */
export async function extractUserDataPackage(): Promise<UserDataPackage> {
  const cards = await defaultDb.cards.toArray();
  const settings = await defaultDb.settings.toArray();
  const studyLogs = await defaultDb.studyLogs.toArray();
  const syncState = getSyncState();

  // 只提取有用户数据的卡片（有历史记录或有最佳版本）
  // 私有版：只同步 topic-dictation 模式的写作卡片
  const cardsWithUserData = cards.filter(c => {
    // 基础条件：有用户数据
    const hasHistory = c.history && c.history.length > 0;
    const hasSpeakingBest = !!c.speakingMeta?.bestAnswerSummary;
    const hasWritingBest = !!c.writingMeta?.bestVersion;
    const hasWritingRecall = !!c.writingMeta?.lastRecall;
    // 话题默写卡片：只要有任何学习痕迹就导出（包括 SRS 状态变化）
    const isTopicDictationWithProgress = c.mode === 'topic-dictation' && (
      hasHistory ||
      c.consecutiveGood > 0 ||
      c.interval > 0 ||
      c.isRelearn
    );

    const hasUserData = hasHistory || hasSpeakingBest || hasWritingBest || hasWritingRecall || isTopicDictationWithProgress;

    if (!hasUserData) return false;

    // 私有版写作卡片过滤：只同步 topic-dictation 模式
    if (!isPublicDeploy && c.type === 'writing' && c.mode !== 'topic-dictation') {
      return false;
    }

    return true;
  });

  const cardUserDataList = cardsWithUserData.map(extractCardUserData);

  // 提取完整用户设置
  const settingsRecord = settings.find(s => s.id === 'default') || settings.find(s => s.id === 'app');
  const userSettings: UserSettings = {};
  if (settingsRecord?.dailyLimits) {
    userSettings.dailyLimits = settingsRecord.dailyLimits as UserSettings['dailyLimits'];
  }
  if (settingsRecord?.srsPreferences) {
    userSettings.srsPreferences = settingsRecord.srsPreferences;
  }
  if (settingsRecord?.studyGoal) {
    userSettings.studyGoal = settingsRecord.studyGoal;
  }
  if (settingsRecord?.weeklyGoal) {
    userSettings.weeklyGoal = settingsRecord.weeklyGoal;
  }

  // 计算统计
  const totalReviews = cardUserDataList.reduce(
    (sum, c) => sum + c.history.length,
    0
  );

  const cardsByType: Record<string, number> = {};
  for (const card of cards) {
    cardsByType[card.type] = (cardsByType[card.type] || 0) + 1;
  }

  // 计算本周学习时长
  let weeklyMinutes = 0;
  try {
    const weekStats = await getWeeklyStudyStats(getWeekStart());
    weeklyMinutes = weekStats.totalMinutes;
  } catch (e) {
    console.warn('[extractUserDataPackage] 获取周统计失败:', e);
  }

  // 递增同步版本号（基于当前已知的云端版本）
  const currentSyncVersion = getCloudSyncVersion();
  const newSyncVersion = currentSyncVersion + 1;

  return {
    version: 2,  // v2 包含 studyLogs
    deviceId: getDeviceId(),
    exportedAt: Date.now(),
    lastSyncAt: syncState.lastSyncAt || undefined,
    syncVersion: newSyncVersion,
    cards: cardUserDataList,
    settings: userSettings,
    studyLogs,  // 完整学习时间记录
    summary: {
      totalCards: cards.length,
      totalReviews,
      streakDays: 0, // TODO: 计算连续学习天数
      cardsByType,
      weeklyMinutes,
    },
  };
}

/** 将云端数据合并到本地 */
export async function mergeCloudData(cloudData: UserDataPackage, options: { forceOverwrite?: boolean } = {}): Promise<{
  merged: number;
  conflicts: number;
  skipped: number;
  missingCardIds: string[];
}> {
  const { forceOverwrite = false } = options;
  let merged = 0;
  let conflicts = 0;
  let skipped = 0;
  const missingCardIds: string[] = [];

  // 诊断信息：统计本地卡片数量
  const localCardCount = await defaultDb.cards.count();
  console.log('[mergeCloudData] Starting merge:', {
    cloudCards: cloudData.cards.length,
    localCards: localCardCount,
    deviceId: cloudData.deviceId,
    exportedAt: new Date(cloudData.exportedAt).toISOString(),
  });

  for (const cloudCard of cloudData.cards) {
    // 私有版：只同步 topic-dictation 写作卡片，跳过其他写作卡片
    // topic-dictation 卡片 ID 格式为 topic-dictation-XXX，旧写作卡片为 wrt-XXX
    const isWritingCard = cloudCard.cardId.startsWith('wrt-') || cloudCard.cardId.startsWith('topic-dictation-');
    const isTopicDictation = cloudCard.cardId.startsWith('topic-dictation-');
    if (!isPublicDeploy && isWritingCard && !isTopicDictation) {
      skipped++;
      continue;
    }

    const localCard = await defaultDb.cards.get(cloudCard.cardId);

    if (!localCard) {
      // 本地没有这张卡片，记录并跳过
      missingCardIds.push(cloudCard.cardId);
      skipped++;
      continue;
    }

    // 同步策略：
    // 1. 历史记录始终合并（去重）
    // 2. best 数据合并（取最新的）
    // 3. SRS 状态取 updatedAt 更新的那一方
    // 4. forceOverwrite 模式下强制用云端覆盖（用于导入备份）

    // 合并历史记录
    const mergedHistory = mergeHistory(localCard.history || [], cloudCard.history);
    const historyChanged = mergedHistory.length > (localCard.history?.length || 0);

    // 判断是否需要更新 SRS 状态
    const localIsFreshSeed = (!localCard.history || localCard.history.length === 0) && cloudCard.history.length > 0;
    const shouldUpdateSrs = forceOverwrite || localIsFreshSeed || cloudCard.updatedAt > localCard.updatedAt;

    // 诊断：记录第一张卡片的详细判断过程
    if (merged === 0 && skipped === 0) {
      console.log('[mergeCloudData] First card decision:', {
        cardId: cloudCard.cardId,
        localHistoryLen: localCard.history?.length || 0,
        cloudHistoryLen: cloudCard.history.length,
        mergedHistoryLen: mergedHistory.length,
        historyChanged,
        localIsFreshSeed,
        localUpdatedAt: localCard.updatedAt,
        cloudUpdatedAt: cloudCard.updatedAt,
        shouldUpdateSrs,
        forceOverwrite,
      });
    }

    // 只要有任何变化就更新
    if (shouldUpdateSrs || historyChanged) {
      const updatePayload: Partial<CardRecord> = {
        // 历史记录始终合并
        history: mergedHistory,
      };

      // 只有 shouldUpdateSrs 时才更新 SRS 状态
      if (shouldUpdateSrs) {
        updatePayload.mode = cloudCard.srs.mode;
        updatePayload.ease = cloudCard.srs.ease;
        updatePayload.interval = cloudCard.srs.interval;
        updatePayload.nextReviewDate = cloudCard.srs.nextReviewDate;
        updatePayload.consecutiveGood = cloudCard.srs.consecutiveGood;
        updatePayload.isRelearn = cloudCard.srs.isRelearn;
        updatePayload.relearnBucket = cloudCard.srs.relearnBucket;
        updatePayload.updatedAt = cloudCard.updatedAt;
      }

      // 合并 best 数据到 speakingMeta/writingMeta
      if (cloudCard.best) {
        if (cloudCard.best.bestSummary || cloudCard.best.bestTranscript || cloudCard.best.bestRecordingKey) {
          updatePayload.speakingMeta = {
            ...localCard.speakingMeta,
            bestAnswerSummary: cloudCard.best.bestSummary ?? localCard.speakingMeta?.bestAnswerSummary,
            transcript: cloudCard.best.bestTranscript ?? localCard.speakingMeta?.transcript,
            finalRecordingKey: cloudCard.best.bestRecordingKey ?? localCard.speakingMeta?.finalRecordingKey,
          };
        }
        if (cloudCard.best.bestVersion) {
          updatePayload.writingMeta = {
            ...localCard.writingMeta,
            bestVersion: cloudCard.best.bestVersion,
          };
        }
      }

      // 合并 drafts 数据
      if (cloudCard.drafts?.draftRecordingKey) {
        updatePayload.speakingMeta = {
          ...updatePayload.speakingMeta,
          ...localCard.speakingMeta,
          draftRecordingKey: cloudCard.drafts.draftRecordingKey,
        };
      }

      await defaultDb.cards.update(cloudCard.cardId, updatePayload);
      merged++;
    } else {
      // 没有变化，跳过
      skipped++;
    }
  }

  // 日志输出诊断信息
  console.log('[mergeCloudData] Merge complete:', { merged, conflicts, skipped });
  if (skipped > 0 && merged === 0) {
    console.log('[mergeCloudData] All cards skipped - likely already in sync (no new data from cloud)');
  }
  if (missingCardIds.length > 0) {
    console.warn('[mergeCloudData] Missing card IDs (first 10):', missingCardIds.slice(0, 10));
  }

  return { merged, conflicts, skipped, missingCardIds };
}

/** 根据历史记录计算下次复习日期 */
function computeNextReviewDate(ts: number, intervalDays: number): number {
  // interval 单位是天，转换为毫秒后加到记录时间戳上
  const intervalMs = intervalDays * 24 * 60 * 60 * 1000;
  return ts + intervalMs;
}

/** 合并历史记录（去重） */
function mergeHistory(
  local: CardRecord['history'],
  cloud: LearningRecord[]
): CardRecord['history'] {
  const localTs = new Set(local.map(h => h.ts));
  const merged = [...local];

  for (const cloudRecord of cloud) {
    if (!localTs.has(cloudRecord.ts)) {
      // 云端有本地没有的记录
      // 计算 nextReviewDate：基于记录时间 + interval 天数
      const nextReviewDate = computeNextReviewDate(
        cloudRecord.ts,
        cloudRecord.intervalAfter
      );
      merged.push({
        ts: cloudRecord.ts,
        rating: cloudRecord.rating,
        mode: cloudRecord.mode,
        interval: cloudRecord.intervalAfter,
        ease: cloudRecord.easeAfter,
        nextReviewDate,
        answerMeta: cloudRecord.answer,
        notes: cloudRecord.notes,
      });
    }
  }

  // 按时间排序
  merged.sort((a, b) => a.ts - b.ts);
  return merged;
}
